import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Card, CardContent, CardActions,
  IconButton, Chip, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, Alert, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { colors } from '../../styles/theme.js';
import { api } from '../../api/client.js';
import type { IAliasGroup, IAlias } from '../../../shared/types.js';
import { MatchType } from '../../../shared/types.js';

export default function AliasRegistryPage() {
  const [groups, setGroups] = useState<IAliasGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  // Create Group Dialog
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<IAliasGroup | null>(null);
  const [groupForm, setGroupForm] = useState({ canonical_name: '', description: '' });

  // Add Alias Dialog
  const [aliasDialogOpen, setAliasDialogOpen] = useState(false);
  const [aliasTargetGroupId, setAliasTargetGroupId] = useState<string | null>(null);
  const [aliasForm, setAliasForm] = useState({ alias_value: '', match_type: MatchType.EXACT });

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<IAliasGroup[]>('/config-plus/alias-groups');
      if (res.success) setGroups(res.data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const handleCreateGroup = async () => {
    try {
      const res = editingGroup
        ? await api.patch<IAliasGroup>(`/config-plus/alias-groups/${editingGroup.id}`, groupForm)
        : await api.post<IAliasGroup>('/config-plus/alias-groups', groupForm);
      if (res.success) {
        setGroupDialogOpen(false);
        setEditingGroup(null);
        setGroupForm({ canonical_name: '', description: '' });
        fetchGroups();
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleDeleteGroup = async (id: string) => {
    if (!confirm('Alias-Gruppe wirklich löschen? Alle zugehörigen Aliase werden ebenfalls gelöscht.')) return;
    try {
      await api.delete(`/config-plus/alias-groups/${id}`);
      fetchGroups();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleAddAlias = async () => {
    if (!aliasTargetGroupId) return;
    try {
      const res = await api.post<IAlias>(`/config-plus/alias-groups/${aliasTargetGroupId}/aliases`, aliasForm);
      if (res.success) {
        setAliasDialogOpen(false);
        setAliasForm({ alias_value: '', match_type: MatchType.EXACT });
        fetchGroups();
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleDeleteAlias = async (groupId: string, aliasId: string) => {
    try {
      await api.delete(`/config-plus/alias-groups/${groupId}/aliases/${aliasId}`);
      fetchGroups();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const openEditGroup = (group: IAliasGroup) => {
    setEditingGroup(group);
    setGroupForm({ canonical_name: group.canonical_name, description: group.description ?? '' });
    setGroupDialogOpen(true);
  };

  const openNewGroup = () => {
    setEditingGroup(null);
    setGroupForm({ canonical_name: '', description: '' });
    setGroupDialogOpen(true);
  };

  const matchTypeLabel = (mt: MatchType) => {
    switch (mt) {
      case MatchType.EXACT: return 'Exakt';
      case MatchType.CONTAINS: return 'Enthält';
      case MatchType.REGEX: return 'Regex';
    }
  };

  const filteredGroups = groups.filter((g) =>
    g.canonical_name.toLowerCase().includes(filter.toLowerCase()) ||
    g.aliases?.some((a) => a.alias_value.toLowerCase().includes(filter.toLowerCase()))
  );

  if (loading) return <Typography>Lade Alias Registry...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Alias Registry
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Suchen..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ width: 250 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openNewGroup}
            sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d900' } }}
          >
            Neue Gruppe
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack spacing={2}>
        {filteredGroups.map((group) => (
          <Card key={group.id} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {group.canonical_name}
                </Typography>
                <Box>
                  <Tooltip title="Bearbeiten">
                    <IconButton size="small" onClick={() => openEditGroup(group)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Löschen">
                    <IconButton size="small" color="error" onClick={() => handleDeleteGroup(group.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              {group.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {group.description}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                {group.aliases?.map((alias) => (
                  <Chip
                    key={alias.id}
                    label={`${alias.alias_value}${alias.match_type !== MatchType.EXACT ? ` (${matchTypeLabel(alias.match_type)})` : ''}`}
                    onDelete={() => handleDeleteAlias(group.id, alias.id)}
                    sx={{ borderRadius: '50px' }}
                  />
                ))}
                <Chip
                  icon={<AddIcon />}
                  label="Alias hinzufügen"
                  variant="outlined"
                  onClick={() => { setAliasTargetGroupId(group.id); setAliasDialogOpen(true); }}
                  sx={{ borderRadius: '50px', borderStyle: 'dashed' }}
                />
              </Box>
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'flex-end' }}>
              <Typography variant="caption" color="text.secondary">
                {group.aliases?.length ?? 0} Aliase
                {group.creator && ` · erstellt von ${group.creator.name}`}
              </Typography>
            </CardActions>
          </Card>
        ))}
      </Stack>

      {filteredGroups.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', mt: 6, color: colors.black60 }}>
          <Typography variant="h6">Keine Alias-Gruppen gefunden</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Erstelle eine neue Gruppe, um Scanner-Erkennungen zu normalisieren.
          </Typography>
        </Box>
      )}

      {/* Group Create/Edit Dialog */}
      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? 'Alias-Gruppe bearbeiten' : 'Neue Alias-Gruppe'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Kanonischer Name"
              fullWidth
              value={groupForm.canonical_name}
              onChange={(e) => setGroupForm({ ...groupForm, canonical_name: e.target.value })}
              placeholder="z.B. Google Tag Manager"
            />
            <TextField
              label="Beschreibung (optional)"
              fullWidth
              multiline
              rows={2}
              value={groupForm.description}
              onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={!groupForm.canonical_name}
            sx={{ bgcolor: colors.lime, color: colors.black }}
          >
            {editingGroup ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Alias Dialog */}
      <Dialog open={aliasDialogOpen} onClose={() => setAliasDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Alias hinzufügen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Alias-Wert"
              fullWidth
              value={aliasForm.alias_value}
              onChange={(e) => setAliasForm({ ...aliasForm, alias_value: e.target.value })}
              placeholder="z.B. GTM, gtm.js, googletagmanager.com"
            />
            <FormControl fullWidth>
              <InputLabel>Match-Typ</InputLabel>
              <Select
                value={aliasForm.match_type}
                label="Match-Typ"
                onChange={(e) => setAliasForm({ ...aliasForm, match_type: e.target.value as MatchType })}
              >
                <MenuItem value={MatchType.EXACT}>Exakt (genaue Übereinstimmung)</MenuItem>
                <MenuItem value={MatchType.CONTAINS}>Enthält (Teilstring)</MenuItem>
                <MenuItem value={MatchType.REGEX}>Regex (regulärer Ausdruck)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAliasDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleAddAlias}
            disabled={!aliasForm.alias_value}
            sx={{ bgcolor: colors.lime, color: colors.black }}
          >
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
